// Approved rebuild copy (brief applied site-wide). Keep all public-facing positioning here.

export const ATTRIBUTION = "Selected experience led by PMN's founder before and through the formation of the firm.";

export const HOME_HERO = {
  eyebrow: "Fractional program leadership",
  headline: "Critical initiatives need senior leadership—not always another full-time hire.",
  body:
    "PMN helps regulated, growth-stage, and PE-backed organizations assess complex initiatives, establish the right delivery structure, and lead execution through experienced program leaders and PMN-led teams.",
  primary: "Discuss an Initiative",
  secondary: "How PMN engages",
};

export const POINT_OF_VIEW = {
  eyebrow: "PMN point of view",
  title: "Complex initiatives rarely fail for lack of plans. They fail when no one senior is accountable for execution.",
  paragraphs: [
    "When a critical initiative is launched, organizations usually face the same choice: hire a permanent senior leader the business may not need once the work is done, or bring in a large consultancy that bills for scale and distance.",
    "PMN was built for the space between those options — senior program leadership scoped to the initiative, accountable to the executive sponsor, and backed by a firm rather than a single consultant.",
  ],
};

export const SITUATIONS = {
  eyebrow: "When organizations bring in PMN",
  title: "Situations we are brought into.",
  items: [
    "A critical initiative is behind schedule, over budget, or losing executive confidence.",
    "A regulatory commitment, audit finding, or control remediation has a fixed date and no clear owner.",
    "A PE-backed or growth-stage company is scaling faster than its delivery structure can support.",
    "A transformation or platform program spans functions that do not report to the same leader.",
    "A PMO exists on paper but not in practice — status reads green until the milestone is missed.",
    "Leadership needs an independent assessment before committing more capital or more people.",
  ],
};

export const SERVICES = [
  {
    id: "assessment",
    n: "01",
    title: "Assessment and Roadmap",
    summary:
      "A focused, time-boxed review of an initiative, program, or portfolio: where it actually stands, what is at risk, and what it will take to deliver.",
    detail:
      "PMN interviews sponsors and teams, reviews plans, governance, and delivery data, and returns a candid, decision-ready picture. The engagement ends with a prioritized roadmap and a recommended delivery structure — whether or not PMN leads what follows.",
    bullets: [
      "Independent view of status, risk, and root causes",
      "Governance, decision-rights, and cadence review",
      "Prioritized roadmap and recommended delivery structure",
      "Executive readout with clear options and trade-offs",
    ],
    fit: "Best when leadership needs clarity before committing more capital, or when an in-flight program has lost confidence.",
  },
  {
    id: "fractional-leadership",
    n: "02",
    title: "Fractional Program Leadership",
    summary:
      "A senior PMN program leader embedded with your executive sponsor and teams, owning delivery of a critical initiative for the duration of the work.",
    detail:
      "The program leader runs governance, cadence, risk, stakeholder alignment, and decisions — in the room, not from arm's length. Commitment is scoped to the initiative and scales up or down as the work demands, with a defined end state and a clean transition.",
    bullets: [
      "Program governance, cadence, and executive reporting",
      "Risk, dependency, and decision management",
      "Stakeholder alignment across functions and vendors",
      "Transition and handover to the permanent organization",
    ],
    fit: "Best for initiatives that need senior ownership now, without adding a permanent executive role.",
  },
  {
    id: "delivery-teams",
    n: "03",
    title: "PMN-Led Delivery Teams",
    summary:
      "When an initiative needs more than one leader, PMN assembles and leads a right-sized team of experienced delivery operators under PMN accountability.",
    detail:
      "PMN remains accountable for executive alignment, delivery quality, and outcomes while experienced operators lead day-to-day execution — program and project management, PMO operations, business analysis, and change management as the work requires.",
    bullets: [
      "PMN program leadership plus experienced delivery operators",
      "Single point of accountability for the sponsor",
      "Team shaped to the initiative and adjusted as it evolves",
      "Quality, cadence, and reporting standards set and held by PMN",
    ],
    fit: "Best for multi-workstream programs, PMO stand-ups, and recoveries that need capacity and leadership together.",
  },
];

export const COMMERCIAL = {
  eyebrow: "How engagements are structured",
  title: "Structured around the work, not the clock.",
  body:
    "Engagements may be structured as a fixed fee, a monthly retainer, milestone-based, or time and materials, depending on the work. Scope, commitment level, and end state are agreed up front, and each engagement is priced against the fully-loaded cost of the senior leadership it replaces.",
};

export const WHY_FRACTIONAL = {
  eyebrow: "Why fractional and FTE-competitive economics",
  title: "Senior leadership, scoped to the initiative.",
  body:
    "A full-time senior hire carries fixed cost, hiring lag, and ramp time — and often outlasts the need that created the role. A large consultancy brings scale you may not need and overhead you will pay for. Fractional program leadership places a senior operator on the initiative at FTE-competitive cost, for as long as the work requires, with a defined end.",
  pillars: [
    { title: "Immediate activation", body: "Senior leadership engaged in weeks, not the months a search and ramp cycle takes." },
    { title: "Right-sized commitment", body: "Commitment scales with the initiative — up during critical phases, down as it stabilizes." },
    { title: "Defined end state", body: "Every engagement has an agreed exit and transition, so cost does not persist after the work is done." },
  ],
};

export const WHY_PMN = {
  eyebrow: "Why PMN",
  title: "What makes PMN different.",
  items: [
    { title: "Led by operators, not advisors", body: "PMN program leaders have run enterprise programs from the inside — they own cadence, decisions, and outcomes rather than recommending from a distance." },
    { title: "Accountable to the sponsor", body: "Every engagement has a named PMN leader accountable to the executive sponsor for delivery quality and outcomes." },
    { title: "A firm, not a single consultant", body: "PMN remains accountable for executive alignment, delivery quality, and outcomes while experienced operators lead day-to-day execution." },
    { title: "Built for regulated environments", body: "Governance, evidence, and control-oriented execution are part of how PMN runs programs — not an overlay added when the auditors arrive." },
  ],
};

export const EXPERIENCE = {
  eyebrow: "Properly attributed experience",
  title: "The delivery record behind the firm.",
  attribution: ATTRIBUTION,
  stats: [
    { value: "$100M+", label: "in digital strategy programs led" },
    { value: "$15M+", label: "in cost savings realized for employers and clients" },
    { value: "98%", label: "on-time delivery across the portfolios led" },
    { value: "12+", label: "years leading enterprise programs" },
  ],
  note:
    "These figures reflect programs led by Brandon Rodgers at Citi, Cook Children's Health Care System, and in earlier roles — not PMN client engagements. They are presented to show the experience PMN's model is built on.",
};

export const CAPABILITY_AREAS = [
  { id: "assessment-recovery", title: "Program assessment and recovery", body: "Independent review of an initiative's true status, root causes, and recovery options — followed, where needed, by hands-on stabilization and re-planning." },
  { id: "initiative-leadership", title: "Critical initiative leadership", body: "Senior program leadership for the initiatives that cannot miss: transformations, platform programs, integrations, and commitments made to boards and regulators." },
  { id: "pmo-capability", title: "PMO and delivery capability development", body: "Standing up or resetting a PMO that works in practice — governance, cadence, reporting, tooling, and the operating habits that keep it honest." },
  { id: "portfolio-governance", title: "Portfolio governance and strategic alignment", body: "Connecting the portfolio to strategy: prioritization, funding and decision rights, dependency management, and executive reporting leadership can act on." },
  { id: "team-enablement", title: "Team enablement and delivery training", body: "Practical coaching and training for program managers, PMO staff, and sponsors, so delivery discipline stays after PMN transitions out." },
  { id: "delivery-teams", title: "PMN-led delivery teams", body: "Right-sized teams of experienced operators — program and project management, PMO operations, business analysis, change management — led and quality-assured by PMN." },
  { id: "regulatory-execution", title: "Regulatory and control-oriented execution", body: "Delivery that produces the evidence regulators and auditors expect: traceable decisions, documented controls, and remediation delivered to committed dates." },
];

export const CAPABILITIES_INTRO = {
  eyebrow: "Capabilities",
  title: "Where PMN leads.",
  body: "Seven areas of practice, all in service of one outcome: critical initiatives delivered with senior ownership, in environments where governance and evidence matter.",
};

export const PMN_CLIENT_ENGAGEMENTS = {
  eyebrow: "PMN client engagements",
  title: "Client engagements, published with client approval.",
  body:
    "PMN publishes client engagement summaries only with the client's approval, and never presents a client's work as its own without that consent. Approved summaries will appear here as engagements conclude. For a confidential discussion of relevant work, start a conversation.",
};

export const LEADERSHIP_EXPERIENCE = {
  eyebrow: "Leadership experience behind PMN",
  title: "The programs that shaped PMN's model.",
  attribution: ATTRIBUTION,
  note: "The engagements below were led by Brandon Rodgers while employed by Citi, Cook Children's Health Care System, and other employers. They are not PMN client engagements.",
};

export const ABOUT = {
  firmTitle: "A firm built to extend senior program leadership beyond a single consultant.",
  paragraphs: [
    "PMN was legally established in 2020 and became Brandon Rodgers' full-time focus in 2026. The firm serves regulated, growth-stage, and PE-backed organizations that need senior ownership of critical initiatives without adding a permanent executive role or engaging a large consultancy.",
    "PMN was built to extend senior program leadership beyond a single consultant. PMN remains accountable for executive alignment, delivery quality, and outcomes while experienced operators lead day-to-day execution.",
    "The model is grounded in Brandon's own delivery record — most recently as SVP, Global Program Management Lead at Citi, leading transformation across 13+ cross-functional groups — and in a simple conviction: organizations should not have to choose between the cost of a senior full-time hire and the overhead of a consultancy.",
  ],
  founderTitle: "Founder & Managing Member",
};

export const FOOTER_DESCRIPTION =
  "Senior program leadership and PMN-led delivery teams for critical initiatives in regulated and growth-stage organizations.";
